import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { generateResponsiveStyles } from '@hrworks/design-system/functions';
import { lineClamp, mq, overflowEllipsis, overflowHyphens, shouldForwardProp } from '@hrworks/design-system/utilities';
import type { Color, Typography } from '@hrworks/sui-shared/types';

import type { TextProps } from './Text.types';

const interactiveTextStyles = (theme: Theme) =>
  css({
    cursor: 'pointer',
    textDecoration: 'underline',
    ':focus-visible': {
      outline: `2px solid ${theme.eclipseTier2Color.border.focus}`,
    },
    [mq.supportsHover]: {
      ':not(:disabled):hover': {
        textDecoration: 'none',
      },
    },
  });

type TextStyledProps = {
  $variant: TextProps['variant'];
  $hover: TextProps['hover'];
  $fullWidth: TextProps['fullWidth'];
  $color: TextProps['color'];
  $underlined: TextProps['underlined'];
  $italic: TextProps['italic'];
  $textAlign: TextProps['textAlign'];
  $typography?: Typography;
  $href: TextProps['href'];
  $overflowBehaviour: NonNullable<TextProps['overflowBehaviour']>;
  $lineClamp: TextProps['lineClamp'];
};

const colorMap: Record<Color, (theme: Theme) => string> = {
  primary: (theme) => theme.eclipseTier2Color.text.brand.default,
  secondary: (theme) => theme.eclipseTier2Color.text.subtle,
  warning: (theme) => theme.eclipseTier2Color.text.warning.default,
  success: (theme) => theme.eclipseTier2Color.text.success.default,
  danger: (theme) => theme.eclipseTier2Color.text.error.default,
  info: (theme) => theme.eclipseTier2Color.text.info.default,
};

const Text = styled('span', {
  shouldForwardProp: (prop) => prop !== 'as' && shouldForwardProp(prop),
})<TextStyledProps>(
  ({
    theme,
    $href,
    $textAlign,
    $overflowBehaviour,
    $lineClamp,
    $typography,
    $fullWidth,
    $color,
    $italic,
    $underlined,
    $variant,
    $hover,
  }) => [
    $href && interactiveTextStyles(theme),
    $textAlign &&
      generateResponsiveStyles({
        value: $textAlign,
        cssProp: 'textAlign',
      }),
    $overflowBehaviour === 'ellipsis' && overflowEllipsis,
    $overflowBehaviour === 'break' && overflowHyphens,
    $lineClamp && lineClamp($lineClamp),
    {
      // TODO: Migrate animationDuration to eclipse tokens (no eclipse motion tokens yet)
      transition: `opacity ${theme.marko.variables.animationDuration.normal}`,
      color: 'currentcolor',

      ...($typography && theme.eclipseTier2Typography[$typography]),

      ...($fullWidth && {
        display: 'block',
        width: '100%',
      }),
      ...($color && {
        color: colorMap[$color](theme),
      }),
      ...($italic && {
        fontStyle: 'italic',
      }),
      ...($underlined && {
        textDecoration: 'underline',
      }),
      ...($variant === 'subtle' && {
        // TODO: Migrate opacity to eclipse approach
        opacity: theme.marko.variables.opacity.medium,
      }),
      [mq.supportsHover]: {
        ...($hover && {
          ':hover': {
            // TODO: Migrate opacity to eclipse approach
            opacity: $variant === 'subtle' ? theme.marko.variables.opacity.high : theme.marko.variables.opacity.medium,
          },
        }),
      },
    },
  ],
);

export const S = {
  interactiveTextStyles,
  Text,
} as const;
